/**
 * English fallback text for the backend's message keys.
 *
 * The backend translates nothing: an action result arrives as a Locize key plus positional
 * parameters (`aidispatcher.action.error.offer_above_ceiling`, `['45000', '40000']`). Until that
 * key is filled in Locize there is no sentence to render, and the generic fallback — the key
 * followed by its parameters — hides the very thing the reader needs: *the best offer was 45 000
 * and your rule's maximum was 40 000*.
 *
 * So this is a fallback, never a source of truth: `tr()` uses the Locize translation the moment
 * one exists, in the reader's own language, and only reaches for this when it does not. It does
 * not undo the feature's rule that no fact, operator, action or enum value is hard-coded — those
 * still come from `/metadata`; what is written here is only our own wording for messages the
 * backend already defines.
 *
 * `{{0}}` is `params[0]` — the positions are the backend's, documented on `ActionMessage`.
 */
export const MESSAGE_DEFAULTS: Record<string, string> = {
  // --- refusals shared by the carrier actions -------------------------------------------------
  'aidispatcher.action.error.expedition_not_found': 'The expedition no longer exists.',
  'aidispatcher.action.error.already_has_carrier': 'The expedition already has a carrier.',
  'aidispatcher.action.error.expedition_archived': 'The expedition is archived.',
  'aidispatcher.action.error.expedition_in_state': 'The expedition is {{0}}.',
  'aidispatcher.action.error.loading_in_past': 'Loading was already due at {{0}}.',
  'aidispatcher.action.error.no_active_distribution': 'There is no open distribution on this expedition.',
  'aidispatcher.action.error.no_offers': 'No carrier has offered a price.',

  // --- parameters ------------------------------------------------------------------------------
  'aidispatcher.action.error.missing_parameter': 'The rule does not set "{{0}}".',
  'aidispatcher.action.error.price_not_an_amount': 'The price is not an amount.',
  'aidispatcher.action.error.price_negative': 'The price is negative.',
  'aidispatcher.action.error.no_currency': 'No currency was given.',
  'aidispatcher.action.error.unknown_strategy': 'There is no ranking strategy called "{{0}}".',
  'aidispatcher.action.error.field_not_writable': 'The field "{{0}}" cannot be written.',
  'aidispatcher.action.error.no_such_user_in_company': 'There is no such user in this company ({{0}}).',
  'aidispatcher.action.error.no_such_folder_in_company': 'There is no such folder in this company ({{0}}).',
  'aidispatcher.action.error.expected_true_or_false': '"{{0}}" is not true or false.',
  'aidispatcher.action.error.no_recipients': 'No recipients were given.',
  'aidispatcher.action.error.no_carriers_given': 'No carriers were given.',
  'aidispatcher.action.error.not_a_role': 'There is no role called "{{0}}".',
  'aidispatcher.action.error.extra_cost_not_accepted': 'The expedition is {{0}} — an extra cost needs a settled carrier, and cannot be added once cancelled or handed over.',
  'aidispatcher.action.error.amount_or_percent': 'Give exactly one of an amount and a percentage.',
  'aidispatcher.action.error.percent_not_a_number': 'The percentage is not a number.',
  'aidispatcher.action.error.no_price': 'The tariff does not price this expedition and the rule gives no price to fall back on.',
  'aidispatcher.action.error.no_agreed_price': 'There is no agreed price to take a percentage of, or the total is not in one currency.',

  // --- what an action did ----------------------------------------------------------------------
  'aidispatcher.action.detail.logged': '{{0}}',
  'aidispatcher.action.detail.field_set': 'Set {{0}} to {{1}}.',
  // {{0}} arrives already labelled through `extra_cost.enum.*` — see `PARAM_LABELS` in i18nKeys.
  'aidispatcher.action.detail.extra_cost_added': 'Added extra cost {{0}}: {{1}} {{2}}.',
  'aidispatcher.action.detail.email_queued': 'E-mail queued to {{0}}.',
  'aidispatcher.action.detail.users_notified': 'Notified {{1}} person(s) responsible as {{0}}.',
  'aidispatcher.action.detail.nobody_in_role': 'Nobody holds the role {{0}} on this expedition.',
  'aidispatcher.action.detail.carrier_assigned': 'Assigned {{0}} for {{1}} {{2}}.',
  'aidispatcher.action.detail.distribution_published': 'Published a demand to {{0}} carrier(s) and {{1}} group(s), open until {{2}}.',
  'aidispatcher.action.detail.distribution_published_with_price': 'Published a demand for {{3}} {{4}} to {{0}} carrier(s) and {{1}} group(s), open until {{2}}.',
  // {{0}} arrives already labelled through `append_note_to_*` — see `PARAM_LABELS` in i18nKeys.
  'aidispatcher.action.detail.note_appended': 'Added to the {{0}}: {{1}}',
  'aidispatcher.action.detail.note_unchanged': 'The {{0}} already contains this text.',
  'aidispatcher.action.detail.no_carrier_scored': 'No carrier reached the minimum score {{0}}.',
  'aidispatcher.action.detail.carriers_suggested': 'Suggested carriers ({{0}} route match): {{1}}',
  'aidispatcher.action.detail.offer_awarded': 'Awarded {{0}} for {{1}} {{2}}.',
  'aidispatcher.action.detail.offer_proposed': 'Suggests {{0}} for {{1}} {{2}} — {{3}} of {{4}} carriers responded.',

  // --- why an action would not --------------------------------------------------------------
  'aidispatcher.action.error.no_carrier_scored_for_route': 'No carrier reached the minimum score {{0}} on a {{1}} route match.',
  'aidispatcher.action.error.too_late_to_auction': 'Only {{0}} minute(s) left before loading — too late to open an auction.',
  // The one a dispatcher meets most: the ceiling belongs in the sentence, not in a parameter list.
  'aidispatcher.action.error.offer_above_ceiling': 'The best offer is {{0}} and the rule allows at most {{1}}.',
  // Whatever the underlying service said. Not ours to translate, so it is passed through as is.
  'aidispatcher.action.error.service_refused': '{{0}}',
  'aidispatcher.action.error.action_threw': 'The action failed: {{0}}',
  'aidispatcher.action.error.unknown_action': 'The engine does not know this action.',
  'aidispatcher.action.error.rolled_back_with_rule': 'Rolled back, because a later action in the rule failed.',
};
